import { type InputHTMLAttributes, type ReactElement, type ReactNode, useState } from 'react';
import { WordmarkOnDark } from './brand';
import { EyeIcon, EyeOffIcon, UiIcon, type UiIconName } from './icons';

/**
 * The showcase sign-in shared by every Vello web system (Admin, Central, PDV, Frota, Merchant) — the Claude Design
 * "16 Login - Padrões" direction **1B** ("Vitrine clara · sidebar invertida"): a 520px navy panel on the left with
 * the white wordmark, the form and a host/© foot, and the subsystem's illustrated scene filling the rest of the
 * screen. Each app passes its own scene; the panel, the form controls and their navy palette live here (CSS in
 * `theme.css`, `.vello-auth-showcase*`), so the five logins cannot drift apart.
 *
 * Inside the panel the theme re-points the surface/text/border/action roles at their `on-inverse` counterparts, so
 * any auth content (recovery, session expired, a `Notice`, a primary `Button`) renders in the navy palette without
 * knowing it. Overlays that must keep the light palette (the MFA modal) are portalled out of the panel.
 */
export function AuthShowcase({ scene, host, children }: { scene: string; host?: string; children: ReactNode }): ReactElement {
  const where = host ?? (typeof window !== 'undefined' ? window.location.host : '');
  return (
    <div className="vello-auth-showcase">
      <main className="vello-auth-showcase__panel">
        <div className="vello-auth-showcase__top">
          <WordmarkOnDark height={26} />
        </div>
        <div className="vello-auth-showcase__body">{children}</div>
        <footer className="vello-auth-showcase__foot">
          <span>{where}</span>
          <span>© {new Date().getFullYear()} Vello</span>
        </footer>
      </main>
      <div className="vello-auth-showcase__scene" aria-hidden="true">
        <img src={scene} alt="" decoding="async" />
      </div>
    </div>
  );
}

/** The sign-in title (34px, tight) and its one-line lede. */
export function AuthHeading({ title, subtitle }: { title: string; subtitle?: string }): ReactElement {
  return (
    <div className="vello-auth-heading">
      <h1 className="vello-auth-heading__title">{title}</h1>
      {subtitle !== undefined && <p className="vello-auth-heading__subtitle">{subtitle}</p>}
    </div>
  );
}

type FieldInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className' | 'style'>;

/** A labelled 52px auth field with the design's leading glyph (mail, lock, …). */
export function AuthTextField({ id, label, icon, ...input }: { id: string; label: string; icon: UiIconName } & FieldInputProps): ReactElement {
  return (
    <div className="vello-auth-group">
      <label htmlFor={id} className="vello-auth-label">
        {label}
      </label>
      <div className="vello-auth-field">
        <span className="vello-auth-field__icon">
          <UiIcon name={icon} size={16} />
        </span>
        <input id={id} className="vello-auth-input vello-auth-field__input" {...input} />
      </div>
    </div>
  );
}

/** The password field: lock glyph, monospaced value and the trailing show/hide eye (labels come from the app's locale). */
export function AuthPasswordField({
  id,
  label,
  value,
  onChange,
  showLabel,
  hideLabel,
  autoComplete = 'current-password',
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  showLabel: string;
  hideLabel: string;
  autoComplete?: string;
}): ReactElement {
  const [visible, setVisible] = useState(false);
  return (
    <div className="vello-auth-group">
      <label htmlFor={id} className="vello-auth-label">
        {label}
      </label>
      <div className="vello-auth-field">
        <span className="vello-auth-field__icon">
          <UiIcon name="lock" size={16} />
        </span>
        <input
          id={id}
          className="vello-auth-input vello-auth-field__input vello-auth-field__input--secret"
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
        />
        <button type="button" className="vello-auth-field__eye" onClick={() => setVisible((v) => !v)} aria-label={visible ? hideLabel : showLabel} aria-pressed={visible}>
          {visible ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
        </button>
      </div>
    </div>
  );
}
